import { useState, useEffect } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useRole, useUpdateRole, usePermissions } from "@/lib/api/roles";
import { useAbility } from "@/lib/casl/ability-context";
import { PermissionGrid } from "./permission-grid";
import {
  DataAccessSection,
  DATA_ACCESS_RESOURCES,
} from "./data-access-section";

export function RoleDetailPage() {
  const { roleId } = useParams({ from: "/_authenticated/roles/$roleId" });
  const navigate = useNavigate();
  const ability = useAbility();
  const canEditRoleDefinitions = ability.can("manage", "all");
  const { data: role, isLoading: roleLoading } = useRole(roleId);
  const { data: allPermissions, isLoading: permsLoading } = usePermissions();
  const updateRole = useUpdateRole(roleId);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<
    Set<string>
  >(new Set());

  // Populate form when role data loads
  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description || "");
      setSelectedPermissionIds(new Set(role.permissions.map((p) => p.id)));
    }
  }, [role]);

  const handleTogglePermission = (permissionId: string) => {
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) {
        next.delete(permissionId);
      } else {
        next.add(permissionId);
      }
      return next;
    });
  };

  const handleSave = () => {
    updateRole.mutate({
      name: role?.is_system ? undefined : name.trim(),
      description: description.trim() || null,
      permissionIds: [...selectedPermissionIds],
    });
  };

  if (roleLoading || permsLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Role not found</p>
      </div>
    );
  }

  return (
    <div className="px-8 py-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/roles" })}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 size-4" />
          Back to Roles
        </Button>

        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{role.name}</h1>
          {role.is_system && <Badge variant="destructive">System Role</Badge>}
        </div>
      </div>

      <div className="space-y-6">
        {/* Name & Description */}
        <div className="grid max-w-lg gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={role.is_system || !canEditRoleDefinitions}
            />
            {role.is_system && (
              <p className="text-xs text-muted-foreground">
                System role names cannot be changed.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canEditRoleDefinitions}
            />
          </div>
        </div>

        {allPermissions && (
          <DataAccessSection
            permissions={allPermissions}
            selectedIds={selectedPermissionIds}
            onToggle={canEditRoleDefinitions ? handleTogglePermission : () => {}}
            disabled={!canEditRoleDefinitions}
          />
        )}

        {/* Permission Grid */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">Permissions</h2>
          <p className="text-sm text-muted-foreground">
            Select which actions this role can perform on each resource.
          </p>
          {allPermissions && (
            <PermissionGrid
              permissions={allPermissions.filter(
                (p) =>
                  !(
                    DATA_ACCESS_RESOURCES.includes(
                      p.resource as (typeof DATA_ACCESS_RESOURCES)[number],
                    ) && p.action === "read"
                  ),
              )}
              selectedIds={selectedPermissionIds}
              onToggle={canEditRoleDefinitions ? handleTogglePermission : () => {}}
              lockedIds={
                role.name === "superadmin"
                  ? new Set(
                      allPermissions
                        .filter(
                          (p) =>
                            p.resource === "system" &&
                            p.action === "manage-all",
                        )
                        .map((p) => p.id),
                    )
                  : undefined
              }
            />
          )}
        </div>

        {canEditRoleDefinitions && (
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={
                updateRole.isPending ||
                (!role.is_system && !name.trim())
              }
            >
              <Save className="mr-2 size-4" />
              {updateRole.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
